// ============================================================================
// OPTIMIZED HEADER COMPONENT - PERFORMANCE ENHANCED
// frontend/src/components/layout/Header.tsx
// ============================================================================

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { AuthUser } from '../../services/auth.service';
import ChangePassword from '../auth/ChangePassword';
import NotificationDropdown from './NotificationDropdown';
import { hrNotificationService } from '../../services/hrNotification.service';
import styles from './Header.module.css';

interface HeaderProps {
  user: AuthUser;
  onLogout: () => void;
  onNavigate?: (page: string, employeeId?: string) => void;
}

const Header: React.FC<HeaderProps> = React.memo(({ user, onLogout, onNavigate }) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  
  const userMenuRef = useRef<HTMLDivElement>(null);
  const userAvatarRef = useRef<HTMLDivElement>(null);
  const notificationRef = useRef<HTMLDivElement>(null);
  const notificationButtonRef = useRef<HTMLButtonElement>(null);

  const toggleUserMenu = useCallback(() => {
    setShowUserMenu(prev => !prev);
    if (showNotifications) {
      setShowNotifications(false);
    }
  }, [showNotifications]);

  const toggleNotifications = useCallback(() => {
    setShowNotifications(prev => !prev);
    if (showUserMenu) {
      setShowUserMenu(false);
    }
  }, [showUserMenu]);

  const handleAdminPanelClick = useCallback(() => {
    if (onNavigate) {
      onNavigate('system-settings');
    }
    setShowUserMenu(false);
  }, [onNavigate]);

  const handleSystemSettingsClick = useCallback(() => {
    if (onNavigate) {
      onNavigate('system-settings');
    }
    setShowUserMenu(false);
  }, [onNavigate]);

  const handleManageUsersClick = useCallback(() => {
    if (onNavigate) {
      onNavigate('employees');
    }
    setShowUserMenu(false);
  }, [onNavigate]);

  const handleSettingsClick = useCallback(() => {
    // Settings logic
  }, []);

  const handleChangePasswordClick = useCallback(() => {
    setShowChangePassword(true);
    setShowUserMenu(false);
  }, []);

  const handleCloseChangePassword = useCallback(() => {
    setShowChangePassword(false);
  }, []);

  const handleLogout = useCallback(() => {
    setShowUserMenu(false);
    onLogout();
  }, [onLogout]);

  const handleUserAvatarError = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const target = e.target as HTMLImageElement;
    
    if (!imageError && target.src !== `${process.env.PUBLIC_URL}/images/humanicon.png`) {
      target.src = `${process.env.PUBLIC_URL}/images/humanicon.png`;
      setImageError(true);
    }
  }, [imageError]);

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);

  const avatarSource = useMemo((): string => {
    return `${process.env.PUBLIC_URL}/images/humanicon.png`;
  }, []);

  // Listen for real-time notifications
  useEffect(() => {
    const unsubscribe = hrNotificationService.onNotifications(
      (notifications) => {
        const unreadCount = notifications.filter(n => !n.isRead).length;
        setNotificationCount(unreadCount);
      }
    );

    return () => unsubscribe();
  }, []);

  // Click outside detection
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showUserMenu &&
        userMenuRef.current &&
        userAvatarRef.current &&
        !userMenuRef.current.contains(event.target as Node) &&
        !userAvatarRef.current.contains(event.target as Node)
      ) {
        setShowUserMenu(false);
      }

      if (
        showNotifications &&
        notificationRef.current &&
        notificationButtonRef.current &&
        !notificationRef.current.contains(event.target as Node) &&
        !notificationButtonRef.current.contains(event.target as Node)
      ) {
        setShowNotifications(false);
      }
    };

    if (showUserMenu || showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showUserMenu, showNotifications]);

  const handleAvatarKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleUserMenu();
    } else if (e.key === 'Escape' && showUserMenu) {
      setShowUserMenu(false);
    }
  }, [toggleUserMenu, showUserMenu]);

  const handleNotificationKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleNotifications();
    } else if (e.key === 'Escape' && showNotifications) {
      setShowNotifications(false);
    }
  }, [toggleNotifications, showNotifications]);

  const userDisplayName = useMemo(() => {
    return user.name || user.displayName || 'USER';
  }, [user.name, user.displayName]);

  return (
    <>
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.logo}>
            <h1>PHOENICIAN TECHNICAL SERVICES LLC</h1>
          </div>

          <div className={styles.searchContainer}>
            <div className={styles.searchBox}>
              <svg className={styles.searchIcon} width="20" height="20" viewBox="0 0 24 24" fill="none">
                <circle cx="11" cy="11" r="8" stroke="#999" strokeWidth="2"/>
                <path d="m21 21-4.35-4.35" stroke="#999" strokeWidth="2"/>
              </svg>
              <input 
                type="text" 
                placeholder="Search Here" 
                className={styles.searchInput}
                autoComplete="off"
              />
            </div>
            
            <div className={styles.headerIcons}>
              <button 
                className={styles.iconButton}
                aria-label="Filter"
                type="button"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M3 6h18M7 12h10m-7 6h4" stroke="#6b7280" strokeWidth="2"/>
                </svg>
              </button>
              
              <div className={styles.notificationContainer}>
                <button 
                  ref={notificationButtonRef}
                  className={`${styles.iconButton} ${showNotifications ? styles.active : ''}`}
                  onClick={toggleNotifications}
                  onKeyDown={handleNotificationKeyDown}
                  aria-label="Notifications"
                  aria-expanded={showNotifications}
                  aria-haspopup="menu"
                  type="button"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" stroke="#6b7280" strokeWidth="2"/>
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" stroke="#6b7280" strokeWidth="2"/>
                  </svg>
                  {notificationCount > 0 && (
                    <span className={styles.notificationBadge}>{notificationCount}</span>
                  )}
                </button>

                {showNotifications && (
                  <div ref={notificationRef}>
                    <NotificationDropdown
                      isOpen={showNotifications}
                      onClose={() => setShowNotifications(false)}
                      onNavigate={onNavigate}
                    />
                  </div>
                )}
              </div>
              
              <button 
                className={styles.iconButton} 
                onClick={handleSettingsClick}
                aria-label="Settings"
                type="button"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="3" stroke="#6b7280" strokeWidth="2"/>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1 1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" stroke="#6b7280" strokeWidth="2"/>
                </svg>
              </button>
            </div>
          </div>

          <div className={styles.userProfile}>
            <div className={styles.userInfo}>
              <span className={styles.userName}>{userDisplayName}</span>
              <span className={styles.userEmail}>{user.email}</span>
            </div>
            
            <div 
              ref={userAvatarRef}
              className={`${styles.userAvatar} ${imageLoaded ? styles.loaded : ''}`}
              onClick={toggleUserMenu}
              onKeyDown={handleAvatarKeyDown}
              role="button"
              tabIndex={0}
              aria-expanded={showUserMenu}
              aria-haspopup="menu"
              aria-label="User menu"
            >
              <img 
                src={avatarSource}
                alt="User Avatar"
                className={styles.avatarImage}
                onError={handleUserAvatarError}
                onLoad={handleImageLoad}
                loading="lazy"
                style={{ objectFit: 'cover', width: '100%', height: '100%' }}
              />
            </div>
            
            {showUserMenu && (
              <div ref={userMenuRef} className={styles.userMenu} role="menu">
                {user.isAdmin && (
                  <>
                    <div className={styles.adminMenuSection}>
                      <div className={styles.menuSectionTitle}>Administration</div>
                      
                      <button 
                        onClick={handleAdminPanelClick} 
                        className={`${styles.menuButton} ${styles.adminMenuItem}`}
                        role="menuitem"
                        type="button"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M12 2l3.09 6.26L22 9l-5 4.87L18.18 22 12 18.77 5.82 22 7 13.87 2 9l6.91-.74L12 2z" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                        Admin Panel
                      </button>

                      <button 
                        onClick={handleSystemSettingsClick} 
                        className={`${styles.menuButton} ${styles.adminMenuItem}`}
                        role="menuitem"
                        type="button"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1 1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                        System Settings
                      </button>

                      <button 
                        onClick={handleManageUsersClick} 
                        className={`${styles.menuButton} ${styles.adminMenuItem}`}
                        role="menuitem"
                        type="button"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2"/>
                          <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                        Manage Users
                      </button>
                    </div>

                    <div className={styles.menuDivider}></div>
                  </>
                )}

                <div className={styles.regularMenuSection}>
                  <button 
                    onClick={handleChangePasswordClick} 
                    className={styles.menuButton}
                    role="menuitem"
                    type="button"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                      <circle cx="12" cy="16" r="1" fill="currentColor"/>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    Change Password
                  </button>

                  <button 
                    onClick={handleLogout} 
                    className={styles.logoutButton}
                    role="menuitem"
                    type="button"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" stroke="currentColor" strokeWidth="2"/>
                      <polyline points="16,17 21,12 16,7" stroke="currentColor" strokeWidth="2"/>
                      <line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {showChangePassword && (
        <ChangePassword 
          user={user}
          onClose={handleCloseChangePassword}
        />
      )}
    </>
  );
});

Header.displayName = 'Header';

export default Header;